import { InvokeThrottle } from '@/common/decorators/invoke-throttle'
import { ParamsClean } from '@/common/decorators/params-clean'
import { Injectable } from '@tanbo/vue-di-plugin'
import { Http } from '@/services/http'

export interface FetchOptions {
  keyword: string // 关键词搜索
  page: number // 页码
  limit: number // 每页数量
  sort?: string // 排序字段
  asc?: boolean // 排序,true-正序，false-逆序
  export?: string // 导出文件类型，空表示不导出，现支持CSV
  /** 硬盘容量档位文案（与 disk_capacity_distribution.capacity 一致）；可与 whole_disk_total_* 二选一由前端组装 */
  disk_capacity?: string
  /** 内存规格档位文案（与 memory_statistics.capacity 一致）；可与 memory_capacity_* 二选一由前端组装 */
  memory_spec?: string
  /** 内存容量最小值（MB） */
  memory_capacity_min?: number
  /** 内存容量最大值（MB） */
  memory_capacity_max?: number
  /** 硬盘总量最小值（MB） */
  whole_disk_total_min?: number

  /** 硬盘总量最大值（MB） */
  whole_disk_total_max?: number
  /** 终端 id 列表（与饼图 cpu_brand_distribution.device_ids 一致；与 cpu 二选一优先本字段） */
  device_ids?: string[]
  /** CPU（与饼图 cpu_brand_distribution.brand_name、列表 cpu 列一致；接口字段名为 cpu） */
  cpu?: string

  /** 主板型号（模糊查询） */
  mainboard?: string
}

/** 饼图抽屉时传入列表的筛选条件（四选一或组合由业务决定，当前为单图单条件） */
export type HardwareListSliceFilters = Partial<
  Pick<FetchOptions, 'memory_spec' | 'mainboard' | 'cpu' | 'disk_capacity' | 'device_ids'>
>

/** 与单终端 /terminal/hardware/get 及列表行字段对齐 */
export interface HardwareItem {
  /** 终端id */
  device_id: string
  /** 分组id */
  group_id: number
  /** 分组名 */
  group_name: string
  /** 计算机名 */
  computer_name: string
  /** 登记用户名 */
  user_mark: string
  /** Mac地址 */
  mac: string
  /** 操作系统 */
  system_name: string
  /** 计算机型号 */
  computer_model: string
  /** 产品id */
  product_id: string
  /** cpu */
  cpu: string
  /** 内存规格（与统计 memory_statistics.capacity、筛选 memory_spec 一致，单位文案由接口给出） */
  memory_spec?: string
  /** 内存容量（MB，展示时换算为 GB） */
  memory_capacity?: number
  /** 内存信息 */
  memory_info: string
  /** 硬盘 */
  hard_disk: string
  /** 主板 */
  main_bord: string
  /** 显卡 */
  graphics_card: string
  /** 网卡 */
  network_card: string
  /** 声卡 */
  audio_card: string
  /** 显示器 */
  display: string
  /** sn码 */
  sn_code: string
  /** 内网ip */
  system_ip: string
  /** 变更记录数 */
  change_count: number
  /** 外网IP */
  client_ip: string
  /** 本次开机时间 */
  current_startup_time?: number
  /** 上次关机时间 */
  last_offline_time?: number
  /** 登录状态 */
  login_status?: number
  /** 系统类型 1-windows 2-linux */
  system_type?: number
}

/** POST /terminal/hardware/list 响应体 */
export interface HardwareListResponse {
  total: number
  list: HardwareItem[]
}

export interface ChangeRecordOptions {
  device_id: string // 终端id
  page: number // page
  limit: number // 每页数量
  /** 秒级时间戳，与 useParseTime 一致 */
  start_time?: number | string
  end_time?: number | string
  /** 内存容量最小值（MB） */
  memory_capacity_min?: number
  /** 内存容量最大值（MB） */
  memory_capacity_max?: number
  /** 硬盘总容量最小值（MB） */
  whole_disk_total_min?: number
  /** 硬盘总容量最大值（MB） */
  whole_disk_total_max?: number
}

export interface ChangeRecordItem {
  time: string // 变更时间，时间戳(秒)
  ip: string // ip,以英文逗号分隔
  change_item: string // 变更项
  /** 部分接口返回变更类型枚举，与 change_item 二选一展示 */
  change_type?: number
  origin_setting: string // 原配置
  new_setting: string // 新配置
}
export interface CpuBrandDistribution {
  /** 品牌名称 */
  brand_name: string
  /** 对应终端数量 */
  count: number
  /** 该品牌下终端 id（用于列表 device_ids 精确查询） */
  device_ids: string[]
}

export interface MemoryStatistics {
  /** 内存容量 */
  capacity: string
  /** 对应终端数量 */
  count: number
}

export interface DiskCapacityDistribution {
  /** 硬盘容量 */
  capacity: string
  /** 对应终端数量 */
  count: number
}

export interface MainboardTop5 {
  /** 主板型号 */
  model: string
  /** 对应终端数量 */
  count: number
}

export interface statisticsResponseData {
  /** 终端总数 */
  total_terminal: number
  /** CPU品牌分布 */
  cpu_brand_distribution: CpuBrandDistribution[]
  /** 内存规格统计 */
  memory_statistics: MemoryStatistics[]
  /** 今日硬件变更终端数 */
  today_changed_terminal: number
  /** 硬盘容量分布 */
  disk_capacity_distribution: DiskCapacityDistribution[]
  /** 常见主板TOP5 */
  mainboard_top5: MainboardTop5[]
}
@Injectable()
export class HardwareService {
  constructor (private http: Http) {

  }

  @ParamsClean()
  @InvokeThrottle()
  fetchHardwareData (options: FetchOptions) {
    return this.http
      .post<HardwareListResponse>('/terminal/hardware/list', options)
  }

  @ParamsClean([0], 1)
  download (type: 0|1, options: FetchOptions, moduleName: string) {
    const params = Object.assign({}, options, {
      file_type: type,
      page: 0, // 不分页
      limit: 0
    })
    return this.http.downloadWithBlob('/terminal/hardware/list/export', params, moduleName)
  }

  @ParamsClean()
  @InvokeThrottle()
  fetchChangeRecordData (options: ChangeRecordOptions) {
    return this.http.post<{
      total: number
      list: ChangeRecordItem[]
    }>('/terminal/hardware-change-record/get', options)
  }

  getTerminalHardwareStatistics () {
    return this.http.post<statisticsResponseData>('/terminal/hardware/statistics', {})
  }

}
